import { Button, Tooltip } from '@heroui/react';
import { ArrowDownTag } from 'iconoir-react';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import i18n from '~/common/helpers/i18n';

export function formatDeploymentTime(timestamp: number): string {
  const deploymentDate = new Date(timestamp);
  const now = moment();
  const diffInMinutes = now.diff(deploymentDate, 'minutes');
  const diffInHours = now.diff(deploymentDate, 'hours');

  if (diffInMinutes < 1) {
    return i18n.t('label.time_just_now');
  }
  if (diffInMinutes < 60) {
    return i18n.t('label.time_minutes_ago', { count: diffInMinutes });
  }
  if (diffInHours < 24) {
    return i18n.t('label.time_hours_ago', { count: diffInHours });
  }
  return deploymentDate.toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric'
  });
}

export type DeploymentTimeProps = {
  timestamp: string;
  deployedBy?: string;
  engineVersion?: string;
};

export function DeploymentTime({
  timestamp,
  deployedBy,
  engineVersion
}: DeploymentTimeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const timestampNumber = parseInt(timestamp, 10);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setIsOpen(false));

  return (
    <div className='flex items-center'>
      <p className='text-body-regular-md text-on-surface-variant'>
        {formatDeploymentTime(timestampNumber)}
      </p>
      <Tooltip
        isOpen={isOpen}
        placement='bottom'
        classNames={{
          base: 'shadow-sm',
          content: 'p-0'
        }}
        offset={1}
        content={
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div
            ref={ref}
            onClick={e => e.stopPropagation()}
            className='flex flex-col gap-2.5 px-2.5 py-1'
          >
            <div>
              <p className='text-label-md text-on-surface-variant'>
                {i18n.t('scenes.runtime.labels.time').toLowerCase()}
              </p>
              <p className='text-overline-regular text-on-surface'>
                {(() => {
                  const date = new Date(timestampNumber);
                  const timeString = date.toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    timeZoneName: 'shortOffset'
                  });
                  return timeString;
                })()}
              </p>
            </div>

            {deployedBy && (
              <div>
                <p className='text-label-md text-on-surface-variant'>
                  {i18n.t('scenes.runtime.labels.deployed_by').toLowerCase()}
                </p>
                <p className='text-overline-regular text-on-surface'>
                  {deployedBy}
                </p>
              </div>
            )}

            {engineVersion && (
              <div>
                <p className='text-label-md text-on-surface-variant'>
                  {i18n.t('scenes.runtime.labels.engine_version').toLowerCase()}
                </p>
                <p className='text-overline-regular text-on-surface'>
                  {engineVersion.replace(/^([^.]*\.[^.]*\.[^.]*).*/, '$1')}
                </p>
              </div>
            )}
          </div>
        }
      >
        <Button
          variant='light'
          data-testid='deployment-date-tooltip-button'
          isIconOnly
          size='sm'
          onPress={() => setIsOpen(!isOpen)}
        >
          <ArrowDownTag className='icon-smallest text-on-surface-variant' />
        </Button>
      </Tooltip>
    </div>
  );
}
